import { get, merge, omit, forIn, toString } from 'lodash'
import { useAcademicExpApi, useSearchState } from './index'
import { useSelector } from '@ggs/store'

/**
 * @typedef {import('../../definitions').CourseSectionsExpListParams} CourseSectionsListParams
 * @typedef {import('../../definitions').CourseSectionExpRecord} CourseSectionRecord
 * @typedef {import('../../definitions').SelectOption} SelectOption
 * @typedef {import('./useSearchState').SearchHandlers} SearchHandlers
 * @typedef {(values: CourseSectionsListParams, formik: any) => Promise<void>} OnSubmitHandler
 */
/**
 * @typedef {Object} CourseSearchState
 * @property {CourseSectionsListParams} currentFilters
 * @property {number} currentPage
 * @property {string} currentPageIndex
 * @property {boolean} loading
 * @property {boolean} hasNext
 * @property {CourseSectionRecord[]} pageResults
 * @property {SelectOption[]} pagesIndexOptions
 * @property {number} resultsCount
 * @property {number} rowsPerPage
 */

/**
 * @typedef {Object} CourseSearchHandlers
 * @property {OnSubmitHandler} onSubmitHandler
 */

/**
 * @typedef {Object} CourseSearchHookProps
 * @property {CourseSectionsListParams} defaultFilters

/**
 * @typedef {Object} CourseSearchHook
 * @property {SearchHandlers & CourseSearchHandlers} handlers
 * @property {CourseSearchState} state
 */

/**
 * Course search handlers and local state business logic.
 * @param {CourseSearchHookProps} props
 * @return {CourseSearchHook}
 */
const useCourseSearch = ({ defaultFilters = null }) => {
  const { getCourseSections } = useAcademicExpApi()
  const academicPeriod = useSelector((state) => state.settings.academicPeriod)

  /**
   * @type {CourseSectionRecord}
   */
  const defaultSearchResults = null

  const supportedFilters = [
    'courseSubject',
    'courseNumber',
    'title',
    'personAttendanceRequired',
    'instructionalFormat',
    'deliveryMode',
    'creditUnits',
    'academicPeriodId',
  ]

  /**
   * Process course sections search.
   * @param {CourseSectionsListParams} filterParams
   */
  const searchRequestPromise = async (filterParams) => {
    const title = get(filterParams, 'title', null)
    const keywords = `%${title}%`
    const inPerson = get(filterParams, 'personAttendanceRequired', null)
    // The personAttendanceRequired is not a direct field, is queried against deliveryMode.
    const omitKeys = ['personAttendanceRequired']

    // When filter is set to 'all' option, omit from query filters.
    forIn(filterParams, (filterValue, filterKey) => {
      if (filterValue === 'all' && !omitKeys.includes(filterKey)) {
        omitKeys.push(toString(filterKey))
      }
    })

    const inPersonModes = ['In-Person', 'Hybrid', 'Multi-access']
    const nonInPersonModes = ['Online']

    const finalFilterParams = merge(
      {},
      omit(filterParams, omitKeys),
      title ? { title: keywords } : {},
      inPerson === 'yes'
        ? { deliveryMode: inPersonModes }
        : inPerson === 'no'
        ? { deliveryMode: nonInPersonModes }
        : {},
      {
        orderBy: ['course.courseSubject.code', 'course.courseNumber', 'sectionNumber'],
      },
    )
    return await getCourseSections({
      ...finalFilterParams,
      academicPeriodId: [academicPeriod],
      isNotVisible: false,
    })
  }

  const { handlers, state } = useSearchState({
    defaultFilters,
    supportedFilters,
    defaultSearchResults,
    searchRequestPromise,
  })

  /**
   * Handle search API request.
   * @type {OnSubmitHandler}
   * @param {CourseSectionsListParams} values
   * @param {any} formik
   * @return {Promise<void>}
   */
  const onSubmitHandler = async (values, { setSubmitting }) => {
    await handlers.startNewSearch(values)
    setSubmitting(false)
  }

  return {
    handlers: {
      ...handlers,
      onSubmitHandler,
    },
    state: {
      ...state,
    },
  }
}

export default useCourseSearch
